// BlogPost.jsx - Página de artículo individual
import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { blogPosts } from "../data/blogsposts";
import { relacionadosDe } from "../data/relacionados";
import { injectMidArticleCta } from "../data/ctaArticulo";
import appStoreImg from "../assets/img/app-store.png";
import googlePlayImg from "../assets/img/google.store.png";
import { trackAppStoreClick, trackBlogCtaClick, trackGooglePlayClick } from "../analytics/googleAnalytics";
import "./Blog.css";

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const postContentWithMidCta = injectMidArticleCta(post?.content || "");

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Artículo no encontrado | Wayo Blog</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <main className="blog-page">
          <Header />
          <div className="blog-container" style={{ paddingTop: '120px', textAlign: 'center' }}>
            <h1>Artículo no encontrado</h1>
            <Link to="/blog" className="back-link">Volver al blog</Link>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  // Posts relacionados
  // Los 3 "Sigue leyendo": misma funcion que usa el prerender, asi que el
  // lector y Google ven exactamente los mismos enlaces.
  const relatedPosts = relacionadosDe(post, blogPosts);

  return (
    <>
      {/* La barra superior ya no se pinta aquí: la monta `App.jsx` una sola vez
          para todo el sitio (components/CampanaBar.jsx). Este banner estaba
          copiado en cuatro páginas y ninguna de las tres copias de fuera de la
          portada se enteró de la campaña de septiembre. */}

      <Helmet>
        <title>{post.seoTitle || `${post.title} | Wayo Blog`}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords.join(', ')} />
        <link rel="canonical" href={`https://wayoai.app/blog/${post.slug}`} />

        {/* LÍNEA CLAVE: Forzamos a Google a indexar y a mostrar imágenes grandes */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="googlebot" content="index, follow, max-image-preview:large" />

        {/* Open Graph para redes sociales */}
        <meta property="og:title" content={post.seoTitle || post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:image" content={post.image} />
        <meta property="og:image:alt" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://wayoai.app/blog/${post.slug}`} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags.map((tag, index) => (
          <meta key={index} property="article:tag" content={tag} />
        ))}

        {/* JSON-LD (Schema) mejorado para SEO e IAs */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.seoTitle || post.title,
            "description": post.metaDescription,
            "image": {
              "@type": "ImageObject",
              "url": post.image,
              "width": 1200,
              "height": 630
            },
            "datePublished": post.date,
            "dateModified": post.dateModified || post.date,
            "author": {
              "@type": "Organization",
              "name": post.author || "Wayo Team",
              "url": "https://wayoai.app"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Wayo",
              "logo": {
                "@type": "ImageObject",
                "url": "https://wayoai.app/logo.png"
              }
            },
            "articleSection": post.category,
            "keywords": post.keywords?.join(", "),
            "articleBody": post.content?.replace(/<[^>]*>/g, "") || "",
            "wordCount": post.content?.replace(/<[^>]*>/g, "").split(/\s+/).length || 0,
            "timeRequired": `PT${post.readTime?.split(" ")[0] || "10"}M`
          })}
        </script>
        {post.faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(post.faqSchema)}
          </script>
        )}
      </Helmet>

      <main className="blog-page blog-post-page">
      <Header />
      
      <article
        className="blog-post-content"
        itemScope
        itemType="https://schema.org/Article"
        itemProp="mainEntityOfPage"
      >
        {/* Microdata meta tags to help indexing and explicit indexing signals */}
        <meta itemProp="headline" content={post.seoTitle || post.title} />
        <meta itemProp="description" content={post.metaDescription} />
        <meta itemProp="image" content={post.image} />
        <meta itemProp="datePublished" content={post.date} />
        <div itemProp="author" itemScope itemType="https://schema.org/Person" style={{ display: 'none' }}>
          <meta itemProp="name" content={post.author} />
        </div>
        <div itemProp="publisher" itemScope itemType="https://schema.org/Organization" style={{ display: 'none' }}>
          <meta itemProp="name" content="Wayo" />
        </div>
        <div className="post-header">
          <div className="post-header-content">
            <Link to="/blog" className="back-link">Volver al blog</Link>
            
            <div className="post-meta-header">
              <span className="category-badge">{post.category}</span>
              <span className="post-date">
                {new Date(post.date).toLocaleDateString('es-ES', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <span className="read-time">⏱️ {post.readTime} de lectura</span>
            </div>

            <h1 className="post-title-main">{post.h1 || post.title}</h1>
            
            <div className="post-author-info">
              <div className="author-avatar">
                {post.author.charAt(0)}
              </div>
              <div>
                <p className="author-name">{post.author}</p>
                <p className="author-role">Experto en Nutrición</p>
              </div>
            </div>
          </div>

          <div className="post-image-hero">
            <img src={post.image} alt={post.title} />
          </div>
        </div>

        <div className="post-container">
          <div className="post-main-content">
            {/* Contenido del artículo */}
            <div 
              className="post-body"
              dangerouslySetInnerHTML={{ __html: postContentWithMidCta }}
            />

            <div className="article-download-cta">
              <div className="article-download-copy">
                <span className="article-download-badge">Descarga la app</span>
                <h3>Pasa de la teoria a la practica en 2 segundos</h3>
                <p>
                  No calcules a mano lo que acabas de leer. Deja que la IA de Wayo escanee tu plato y ajuste tus macros por ti.
                </p>
                <Link
                  to="/download"
                  className="article-download-link"
                  onClick={() => trackBlogCtaClick(`blogpost_try_scanner_${post.slug}`)}
                >
                  Probar Escaner Gratis
                </Link>
              </div>
              <div className="article-download-actions">
                <a
                  href="https://apps.apple.com/app/id6756500478"
                  className="blog-store-badge"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackBlogCtaClick(`blogpost_app_store_${post.slug}`);
                    trackAppStoreClick(`blogpost_${post.slug}`);
                  }}
                >
                  <img src={appStoreImg} alt="Descargar en App Store" />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.facundosolis.wayo"
                  className="blog-store-badge"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackBlogCtaClick(`blogpost_google_play_${post.slug}`);
                    trackGooglePlayClick(`blogpost_${post.slug}`);
                  }}
                >
                  <img src={googlePlayImg} alt="Descargar en Google Play" />
                </a>
              </div>
            </div>

            {/* Tags */}
            <div className="post-tags-section">
              <p className="tags-label">Etiquetas:</p>
              <div className="post-tags">
                {post.tags.map((tag, index) => (
                  <Link 
                    key={index} 
                    to={`/blog?tag=${encodeURIComponent(tag)}`} 
                    className="tag"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* CTA
            <div className="post-cta">
              <div className="cta-content">
                <h3>🚀 ¿Listo para alcanzar tus objetivos?</h3>
                <p>Descarga Wayo y empieza a transformar tu salud hoy mismo</p>
                <Link to="/" className="cta-button">
                  Probar Escaner Gratis
                </Link>
              </div>
            </div>
            </div> */}

            {/* Share */}
            <div className="post-share">
              <p className="share-label">Compartir:</p>
              <div className="share-buttons">
                <button 
                  className="share-btn twitter"
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    const text = encodeURIComponent(`Mira este artículo: ${post.title}`);
                    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
                  }}
                >
                  🐦 Twitter
                </button>
                <button 
                  className="share-btn facebook"
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
                  }}
                >
                  👍 Facebook
                </button>
                <button 
                  className="share-btn whatsapp"
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    const text = encodeURIComponent(`Mira este artículo: ${post.title} ${window.location.href}`);
                    window.open(`https://wa.me/?text=${text}`, '_blank');
                  }}
                >
                  💬 WhatsApp
                </button>
                <button 
                  className="share-btn link"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      alert('Enlace copiado al portapapeles');
                    } catch (err) {
                      console.error('Error al copiar:', err);
                      // Fallback para navegadores antiguos
                      const textArea = document.createElement('textarea');
                      textArea.value = window.location.href;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      alert('Enlace copiado al portapapeles');
                    }
                  }}
                >
                  🔗 Copiar link
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="post-sidebar">
            <div className="sidebar-sticky">
              {/* Newsletter Widget */}
              <div className="sidebar-widget">
                <h3>📧 Newsletter</h3>
                <p>Recibe consejos semanales</p>
                <form className="newsletter-form">
                  <input
                    type="email"
                    placeholder="tu@email.com"
                    className="newsletter-input"
                  />
                  <button type="submit" className="newsletter-btn">
                    Suscribirse
                  </button>
                </form>
              </div>

              {/* App Download Widget */}
              <div className="sidebar-widget app-download-widget">
                <div className="app-widget-header">
                  <span className="app-icon">📱</span>
                  <h3>Escanea tu comida</h3>
                </div>
                <p className="app-widget-subtitle">Obtén calorías en 3 segundos</p>

                <div className="app-features-quick">
                  <div className="feature">
                    <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                      <circle cx="12" cy="13" r="4"></circle>
                    </svg>
                    <span>Escanea con foto</span>
                  </div>
                  <div className="feature">
                    <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="23 4 23 10 17 10"></polyline>
                      <path d="M20.49 15a9 9 0 1 1-2-8.94"></path>
                    </svg>
                    <span>Resultado en 3 segundos</span>
                  </div>
                  <div className="feature">
                    <svg className="feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      <line x1="9" y1="10" x2="15" y2="10"></line>
                      <line x1="9" y1="14" x2="15" y2="14"></line>
                    </svg>
                    <span>Completamente gratis</span>
                  </div>
                </div>

                <div className="app-download-buttons">
                  <a
                    href="https://apps.apple.com/app/id6756500478"
                    className="app-badge"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackAppStoreClick("blog_sidebar")}
                  >
                    <img src={appStoreImg} alt="Descargar en App Store" />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.facundosolis.wayo"
                    className="app-badge"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackGooglePlayClick("blog_sidebar")}
                  >
                    <img src={googlePlayImg} alt="Descargar en Google Play" />
                  </a>
                </div>

                <p className="app-cta-text">
                  <strong>7 días Premium gratis</strong><br/>
                  Escanea sin límites
                </p>
              </div>
            </div>
          </aside>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="related-posts-section">
            <div className="related-container">
              <h2 className="related-title">Artículos relacionados</h2>
              <div className="related-posts-grid">
                {relatedPosts.map(relatedPost => (
                  <Link 
                    key={relatedPost.id} 
                    to={`/blog/${relatedPost.slug}`}
                    className="related-post-card"
                  >
                    <div className="related-image">
                      <img src={relatedPost.image} alt={relatedPost.title} />
                    </div>
                    <div className="related-content">
                      <span className="category-badge">{relatedPost.category}</span>
                      <h3>{relatedPost.title}</h3>
                      <p className="read-time">⏱️ {relatedPost.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </article>
      
      <Footer />
    </main>
    </>
  );
}
